<template>
  <div class="card">
    <div class="card-header px-2 pb-1">
      <h3 class="text-center mb-2">Panel Consensus</h3>
      <h5 class="mb-0 h6" :style="{ color: progressColor }">
        {{ progressText }}
      </h5>
    </div>
    <ProgressBar :value="progress" :color="progressColor"></ProgressBar>
    <div class="card-body overflow-auto">
      <div class="container questionnaire-result">
        <div class="row justify-content-center">
          <div class="col">
            <div
              v-if="localResult && localResult.hasRecommendations()"
              class="questionnaire-result-recommendations"
            >
              <h4>Treatments</h4>
              <ul
                v-for="recommendation in localResult.recommendations"
                :key="recommendation.tool.id"
              >
                <li>
                  <p class="h5">
                    {{ recommendation.tool.name }}

                    <b-icon-dash-square-fill
                      variant="danger"
                      :id="'exclude-treat_' + recommendation.tool.id"
                      class="action-icon"
                      @click="excludeRecommendation(recommendation.tool)"
                      title="Exclude from Recommendations"
                    >
                    </b-icon-dash-square-fill>
                  </p>

                  <p class="h5 font-weight-normal">
                    {{ recommendation.approvalPercentage.toFixed(2) }}%
                    consensus strength
                  </p>
                </li>
              </ul>
              <div
                v-if="localResult.hasSpecialNotes()"
                class="mt-3 mb-5 text-left questionnaire-result-special-notes"
              >
                <h4>Special Notes</h4>
                <ul
                  v-for="specialNote in localResult.specialNotes"
                  :key="specialNote.toolName"
                >
                  <li>
                    <h5>{{ specialNote.toolName }}</h5>
                    <ul>
                      <li
                        v-for="(note, index) in specialNote.notes"
                        :key="index"
                      >
                        {{ note }}
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
            <div v-else class="d-block">
              <div class="h4">Treatments</div>
              <span class="text-center">
                No Recommendations</span
              >
            </div>

          </div>
        </div>

        <div
          v-show="excludedRecommendations.length"
          class="row justify-content-center"
        >
          <div class="col">
            <div class="mt-3 questionnaire-result-recommendations">
              <h4>Excluded Treatments</h4>

              <ul
                v-for="excludedRecommendation in excludedRecommendations"
                :key="excludedRecommendation.id"
              >
                <li>
                  <p class="h5" style="opacity: 70%;">
                    {{ excludedRecommendation.name }}

                    <b-icon-plus-square-fill
                      variant="success"
                      :id="'include-treat_' + excludedRecommendation.id"
                      class="action-icon"
                      @click="includeRecommendation(excludedRecommendation)"
                      title="Include to Recommendations"
                    >
                    </b-icon-plus-square-fill>
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { QuestionnaireResult } from "@/entities/questionaire/QuestionnaireResult";
import { Group } from "@/entities/questionaire/Group";
import { Answer } from "@/entities/questionaire/answers/Answer";
import AppProgressBar from "@/components/AppProgressBar.vue";
import { HttpHandler } from "@/http/HttpHandler";
import { Treatment } from "@/entities/Treatment";
import { analytics } from '@/services/analytics'; // ADD THIS
import store from "@/store/index";

@Component({
  components: {
    ProgressBar: AppProgressBar
  }
})
export default class AppQuestionnaireResult extends Vue {
  private httpHandler: HttpHandler = new HttpHandler(store);
  private progress: number = 0;
  private progressColor: string = "";
  private progressText: string = "";
  startTime: number = 0;
  hasTrackedStart: boolean = false;
  private branchName: string = '';

  private excludedRecommendations: { id: number; name: string }[] = [];

  @Prop()
  branchId!: number | null;

  @Prop()
  groups!: Group[] | null;

  @Prop()
  answers!: Answer[] | null;

  @Prop()
  treatmentHistory!: Treatment[] | null;

  @Prop()
  useOnlyMyOpinion!: boolean | null;

  @Prop()
  efficacyFirst!: boolean | null;

  @Prop()
  completeData: boolean = false;

  @Prop()
  result!: QuestionnaireResult | null;
  private localResult: QuestionnaireResult = new QuestionnaireResult([], []);
  private localCompleteData: boolean = false;

  // ADD THIS METHOD
  async mounted() {
    if (!this.hasTrackedStart && this.branchId) {
      this.branchName = await this.fetchBranchName(this.branchId);
      this.startTime = Date.now();
      this.hasTrackedStart = true;
      analytics.trackQuestionnaireStarted(this.branchId, this.branchName);
    }
  }



  private async fetchBranchName(branchId: number): Promise<string> {
    try {
      const branch = await this.httpHandler.getBranchById(branchId);
      return branch.name || `Branch ${branchId}`;
    } catch (error) {
      console.warn('Could not fetch branch name:', error);
      return `Branch ${branchId}`;
    }
  }
  async excludeRecommendation(treat: { id: number; name: string }) {
    let alreadyAdded = this.excludedRecommendations.find(
      t => t.id === treat.id
    );
    if (!alreadyAdded) {
      this.excludedRecommendations.push(treat);
      await this.updateResults();
    }
  }

  async includeRecommendation(treat: { id: number; name: string }) {
    let alreadyAdded = this.excludedRecommendations.find(
      t => t.id === treat.id
    );
    if (alreadyAdded) {
      this.excludedRecommendations = this.excludedRecommendations.filter(
        t => t.id !== treat.id
      );

      await this.updateResults();
    }
  }

  async updateResults(): Promise<void> {
    let answers = this.answers?.map(x => x.getQuizResultObject()) || [];
    let treatmentHistory = this.treatmentHistory?.filter(h => h.id != 0) || [];

    if (
      !this.branchId ||
      !this.groups ||
      this.groups.length < 1 ||
      (answers.length === 0 && treatmentHistory.length === 0)
    ) {
      this.localResult = new QuestionnaireResult([], []);
      return;
    }
    if (!this.branchName && this.branchId) {
      this.branchName = await this.fetchBranchName(this.branchId);
    }

    // Collect data
    const properties = this.groups
      .map(x => x.properties)
      .reduce((a, b) => a.concat(b));

    console.log("delEx 1:", this.excludedRecommendations);
    console.log("delExSet:", this.$store.getters.getDeleteExcludeRecommendations);
    if(this.$store.getters.getDeleteExcludeRecommendations == true) {
      this.$store.commit('setDeleteExcludeRecommendations', false);
      this.excludedRecommendations = [];
    }
    console.log("delEx 2:", this.excludedRecommendations);

    // ==========================================
    // GA4 TRACKING - QUESTION ANSWERED
    // ==========================================
    const answeredCount = this.answers?.length || 0;
    const totalQuestions = properties.length;
    
    if (answeredCount > 0 && this.branchId) {
      const lastAnswer = this.answers![answeredCount - 1];
      const property = properties.find(p => p.id === lastAnswer.PropertyId);
      
      if (property) {
        analytics.trackQuestionAnswered(
          this.branchId,
          this.branchName,     
          answeredCount,
          property.id,
         property.question, 
          totalQuestions
        );
      }
    }
    // ==========================================

    // Request analyze
    const quizResults = {
      answers: answers,
      treatmentHistory: treatmentHistory,
      excludedRecommendations: this.excludedRecommendations.map(t => t.id)
    };

    this.localResult = await this.httpHandler.postQuizResultObjectsByBranchId(
      this.branchId,
      quizResults,
      this.useOnlyMyOpinion,
      !this.efficacyFirst
    );

    let answersLength = this.answers?.length || 0;
    // Update progress value
    this.progress = (answersLength * 100) / properties.length;

    // Update progress text and color
    if (
      properties.some(
        x => x.isPrimary && this.answers?.every(y => y.PropertyId != x.id)
      )
    ) {
      this.progressColor = "#D9544F";
      this.progressText = "Data Incomplete";
      this.localCompleteData = false;
    } else if (answersLength < properties.length) {
      this.progressColor = "#EFAD4D";
      this.progressText = "Sufficient Data";
      this.localCompleteData = false;
    } else {
      this.progressColor = this.localResult.recommendations.length ? "#5DB75D" : "#D9544F";
      this.progressText = "Data Complete";
      this.localCompleteData = true;

      // ==========================================
      // GA4 TRACKING - QUESTIONNAIRE COMPLETED
      // ==========================================
      if (this.startTime && this.branchId) {
        const durationSeconds = Math.round((Date.now() - this.startTime) / 1000);
        
        analytics.trackQuestionnaireCompleted(
          this.branchId,
          this.branchName,     
          totalQuestions,
          answersLength,
          durationSeconds
        );
      }
      // ==========================================
    }

    // ==========================================
    // GA4 TRACKING - RECOMMENDATIONS VIEWED
    // ==========================================
    if (this.branchId) {
      const dataCompleteness = 
        this.progressText === "Data Incomplete" ? 'incomplete' :
        this.progressText === "Sufficient Data" ? 'sufficient' : 'complete';
      
      analytics.trackRecommendationsViewed(
        this.branchId,
        this.branchName,
        this.localResult.recommendations?.length || 0,
        dataCompleteness
      );
    }
    // ==========================================
  }

  @Watch("answers")
  onAnswersChanged() {
    this.updateResults();
  }

  @Watch("treatmentHistory", { deep: true })
  onTreatmentHistoryChanged() {
    let emptyTreatment = this.treatmentHistory?.find(t => t.id === 0);
    if (emptyTreatment === undefined) {
      this.updateResults();
    }
  }

  @Watch("useOnlyMyOpinion")
  onUseOnlyMyOpinionChanged() {
    this.updateResults();
  }

  @Watch("efficacyFirst")
  onEfficacyFirstChanged() {
    this.updateResults();
  }

  @Watch("result")
  onResultChanged() {
    this.localResult = this.result ?? new QuestionnaireResult([], []);
  }

  @Watch("localResult")
  onLocalResultChanged() {
    this.$emit("update:result", this.localResult);
  }

  @Watch("localCompleteData")
  onLocalCompleteDataChanged() {
    this.$emit("update:completeData", this.localCompleteData);
  }
}
</script>

<style scoped lang="scss">
.card {
  height: 100%;
}

.questionnaire-result {
  &-recommendations {
    font-size: 1.5625rem;
    width: auto;
    display: inline-block;

    &-items {
    }
  }

  &-recommendations,
  &-special-notes {
    padding-left: 0;
  }

  &-special-notes {
    font-size: 1rem;
  }

  &-special-notes__label {
    margin-left: 40px;
  }
}

.action-icon {
  cursor: pointer;
  height: 1rem;
}

</style>
